import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Pencil, Trash2, Copy, GripVertical, Eye, EyeOff, Loader2 } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, CheckF, ImageF, inputCls } from "./AFields";
import { Modal } from "./Participants";

const BLANK = { heading_en: "", heading_hi: "", sub_en: "", sub_hi: "", cta_text_en: "Donate Now", cta_text_hi: "", cta_link: "/donate", image: "", enabled: true, full: false, hotspots: true, alt_text: "" };

function SlideRow({ s, onEdit, onDup, onDel, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: s.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3" data-testid={`slide-row-${s.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab touch-none text-slate-400 hover:text-navy" aria-label="Drag to reorder" data-testid={`slide-drag-${s.id}`}><GripVertical size={16} /></button>
      {s.image ? <img src={s.image} alt="" className="h-12 w-20 rounded-md object-cover" /> : <div className="h-12 w-20 rounded-md bg-navy-50" />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-navy">{s.heading_en || "(no heading)"}</p>
        <p className="truncate text-xs text-slate-500">{s.sub_en}</p>
        <p className="text-[10px] text-slate-400">CTA: {s.cta_text_en} → {s.cta_link}</p>
      </div>
      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${s.enabled !== false ? "bg-brandgreen-100 text-brandgreen" : "bg-slate-100 text-slate-500"}`}>{s.enabled !== false ? "LIVE" : "DISABLED"}</span>
      {s.full && <span className="rounded-full bg-navy px-2.5 py-0.5 text-[10px] font-bold text-saffron-400" data-testid={`slide-full-badge-${s.id}`}>FULL-BLEED</span>}
      <div className="flex gap-1.5">
        <button onClick={() => onToggle(s)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Toggle" data-testid={`slide-toggle-${s.id}`}>{s.enabled !== false ? <EyeOff size={13} /> : <Eye size={13} />}</button>
        <button onClick={() => onEdit(s)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`slide-edit-${s.id}`}><Pencil size={13} /></button>
        <button onClick={() => onDup(s)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Duplicate" data-testid={`slide-dup-${s.id}`}><Copy size={13} /></button>
        <button onClick={() => onDel(s)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`slide-del-${s.id}`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export default function HeroManager() {
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () => api.get("/admin/hero-slides").then((r) => setSlides(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.post("/admin/hero-slides/save", editing); toast.success("Slide saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(slides, slides.findIndex((s) => s.id === active.id), slides.findIndex((s) => s.id === over.id));
    setSlides(next);
    try { await api.post("/admin/hero-slides/reorder", { ids: next.map((s) => s.id) }); toast.success("Order saved"); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const toggle = async (s) => {
    try { await api.post("/admin/hero-slides/save", { ...s, enabled: s.enabled === false }); toast.success(s.enabled === false ? "Slide enabled" : "Slide disabled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const dup = async (s) => {
    try { await api.post(`/admin/hero-slides/${s.id}/duplicate`); toast.success("Slide duplicated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete slide "${s.heading_en}"?`)) return;
    try { await api.delete(`/admin/hero-slides/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="hero-manager">
      <SEO title="Homepage Hero Slider" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Homepage Hero Slider</h1>
          <p className="mt-1 text-sm text-slate-500">Drag to reorder. Only LIVE slides appear on the homepage — disable instead of deleting to hide a slide. Changes go live on save.</p>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="slide-new-btn"><Plus size={15} /> New Slide</button>
      </div>

      {slides.length === 0 && (
        <p className="mt-6 rounded-lg border border-saffron-500/40 bg-saffron-100/60 px-5 py-4 text-sm font-semibold text-navy" data-testid="hero-fallback-note">
          No custom slides yet — the homepage currently shows its built-in default slides. Add slides here to take control of the hero.
        </p>
      )}

      <div className="mt-6 max-w-3xl space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {slides.map((s) => <SlideRow key={s.id} s={s} onEdit={(x) => setEditing({ ...BLANK, ...x })} onDup={dup} onDel={del} onToggle={toggle} />)}
          </SortableContext>
        </DndContext>
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Slide" : "New Slide"} testid="slide-editor" wide>
          {editing.image && (
            <div className="relative mb-5 h-40 overflow-hidden rounded-lg bg-navy" data-testid="slide-preview">
              <img src={editing.image} alt="" className={`h-full w-full ${editing.full ? "object-contain" : "object-cover opacity-60"}`} />
              {!editing.full && (
              <div className="absolute inset-0 flex flex-col justify-center px-6">
                <p className="font-display text-lg font-bold text-white">{editing.heading_en || "Heading"}</p>
                <p className="mt-1 line-clamp-2 text-xs text-slate-200">{editing.sub_en}</p>
                <span className="mt-2 inline-flex w-fit rounded-full bg-saffron-500 px-3 py-1 text-xs font-bold text-navy">{editing.cta_text_en}</span>
              </div>
              )}
            </div>
          )}
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Heading (English)" value={editing.heading_en} onChange={(v) => setEditing({ ...editing, heading_en: v })} required />
            <TextF label="Heading (Hindi)" value={editing.heading_hi} onChange={(v) => setEditing({ ...editing, heading_hi: v })} />
            <AreaF label="Subtext (English)" value={editing.sub_en} onChange={(v) => setEditing({ ...editing, sub_en: v })} rows={2} ai contentType="article" />
            <AreaF label="Subtext (Hindi)" value={editing.sub_hi} onChange={(v) => setEditing({ ...editing, sub_hi: v })} rows={2} ai contentType="article" />
            <TextF label="Button Text (English)" value={editing.cta_text_en} onChange={(v) => setEditing({ ...editing, cta_text_en: v })} />
            <TextF label="Button Text (Hindi)" value={editing.cta_text_hi} onChange={(v) => setEditing({ ...editing, cta_text_hi: v })} />
            <TextF label="Button Link" value={editing.cta_link} onChange={(v) => setEditing({ ...editing, cta_link: v })} placeholder="/donate" />
            <ImageF label="Background Image" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} />
          </div>
          <div className="mt-5 space-y-3">
            <CheckF label="Live on homepage" value={editing.enabled !== false} onChange={(v) => setEditing({ ...editing, enabled: v })} />
            <CheckF label="Full-bleed image — no text overlay (the image itself contains the heading, text and buttons)" value={!!editing.full} onChange={(v) => setEditing({ ...editing, full: v })} />
            {editing.full && <CheckF label="Transparent clickable hotspots over the baked Donate Now / Explore Our Work buttons" value={editing.hotspots !== false} onChange={(v) => setEditing({ ...editing, hotspots: v })} />}
            {editing.full && <p className="rounded-lg bg-saffron-100/60 px-4 py-2.5 text-xs font-semibold text-navy" data-testid="full-bleed-hint">Full-bleed mode: the heading, subtext and button fields above are kept for your records but are NOT rendered over the image. Alt text is used for accessibility.</p>}
          </div>
          {editing.full && (
            <div className="mt-4"><TextF label="Image Alt Text" value={editing.alt_text || ""} onChange={(v) => setEditing({ ...editing, alt_text: v })} placeholder="Sahaj Sarathi Foundation – Education, Health, Social Security and Equal Participation" /></div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="slide-save">
              {busy && <Loader2 size={14} className="animate-spin" />} Save Slide
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
